import BookkeepingManagerStore from 'admin/BookkeepingManagerStore'
import BookkeepingPendingAction from 'admin/bookkeeping/BookkeepingPendingAction'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { action, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'

export default class BookkeepingManagerDetailsViewModel {
  /**
   * Whether we should show the bookkeeping pending action details.
   */
  @observable
  showing = false

  /**
   * Subject used to emit when details is updated.
   */
  private readonly update = new Subject<BookkeepingPendingAction>()

  /**
   * Emits the bookkeeping pending action when details is updated.
   */
  readonly update$ = this.update.asObservable()

  /**
   * The bookkeeping pending action.
   */
  @observable
  bookkeepingPendingAction = BookkeepingPendingAction.none

  constructor(
    private bookkeepingManagerStore: BookkeepingManagerStore,
    private newAlert: AlertFactory,
    private closeNav: () => void,
  ) {
    makeObservable(this)
  }

  /**
   * Sets the bookkeeping pending action.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  setBookkeepingPendingAction(
    bookkeepingPendingAction: BookkeepingPendingAction,
  ) {
    this.bookkeepingPendingAction = bookkeepingPendingAction
  }

  /**
   * Shows the bookkeeping pending action details.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  show(bookkeepingPendingAction: BookkeepingPendingAction) {
    this.showing = true
    this.closeNav()
    this.setBookkeepingPendingAction(bookkeepingPendingAction)
  }

  /**
   * Closes the bookkeeping pending action details.
   */
  @action.bound
  close() {
    this.showing = false

    this.setBookkeepingPendingAction(BookkeepingPendingAction.none)
  }

  /**
   * Confirms the bookkeeping pending action platform access.
   */
  @action.bound
  confirmAccess = task.resolved(async () => {
    return this.bookkeepingManagerStore
      .confirmManagedPlatformAccess(this.bookkeepingPendingAction.id)
      .then(() => this.update.next(this.bookkeepingPendingAction))
      .then(this.notifyAccessConfirmed)
      .then(this.close)
      .catch(this.handleError)
  })

  /**
   * Displays a message notifying the access was confirmed.
   */
  @action.bound
  private notifyAccessConfirmed() {
    this.newAlert()
      .withState(AlertState.Success)
      .saying('You confirmed bookkeeping access.')
      .show()
      .autoDismiss()
  }

  /**
   * Handles error displaying the error message.
   */
  @action.bound
  private handleError(error: Error) {
    this.newAlert().fromError(error).show()
    console.error(error)
  }
}
